import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { getUserListings } from "@/lib/actions";
import { getArchivedListings } from "@/lib/actions/listingActions"; 
import { Button } from "@/components/ui/button";
import { Archive, Plus, XCircle, CheckCircle, AlertTriangle } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import ListingItem from "@/components/user/ListingItem";
import { useListingActions } from "@/hooks/useListingActions";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import PremiumOptions from "@/components/ui/PremiumOptions";
import { Separator } from "@/components/ui/separator";
import ConfirmationDialog from "@/components/user/ConfirmationDialog";
import PremiumConfirmationDialog from "@/components/dialogs/PremiumConfirmationDialog";
import ArchivedListingRestoreDialog from "@/components/dialogs/ArchivedListingRestoreDialog";
import PremiumPayment from "@/components/user/PremiumPayment";
import { Badge } from "@/components/ui/badge";

interface UserListingsProps {
  userId: string;
  isViewOnly?: boolean;
}

const UserListings = ({ userId, isViewOnly = false }: UserListingsProps) => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState("active");
  const isOwner = profile?.id === userId;
  const [selectedListingId, setSelectedListingId] = useState<number | null>(
    null
  );

  const {
    confirmDialogOpen,
    confirmDialogAction,
    premiumDialogOpen,
    premiumConfirmDialogOpen,
    premiumVouchersCount,
    archiveRestoreDialogOpen,
    setArchiveRestoreDialogOpen,
    hasAvailableCredits,
    setConfirmDialogOpen,
    setPremiumDialogOpen,
    setPremiumConfirmDialogOpen,
    handleDeleteListing,
    handleDeactivateListing,
    confirmDelete,
    confirmDeactivate,
    handleTogglePremium, 
    confirmPremiumUpgrade,
    handlePremiumSuccess,
    handleToggleSold,
    confirmToggleSold,
    handleReactivateListing,
    confirmRestoreArchived,
  } = useListingActions({ userId });


  // Fetch user's listings
  const {
    data: userListings = [],
    isLoading,
    error: listingsError,
    refetch: refetchListings,
  } = useQuery({
    queryKey: ["userListings", userId],
    queryFn: async () => {
      return await getUserListings(userId);
    },
    enabled: !!userId,
  });

  // Fetch user's archived listings
  const {
    data: archivedListings = [],
    isLoading: isLoadingArchived,
    error: archivedError,
    refetch: refetchArchived,
  } = useQuery({
    queryKey: ["archivedListings", userId],
    queryFn: async () => {
      const result = await getArchivedListings(userId);
      return result;
    },
    enabled: !!userId, 
  });

  useEffect(() => {
    if (listingsError) {
      console.error("Error fetching user listings:", listingsError);
    }
    if (archivedError) {
      console.error("Error fetching archived listings:", archivedError);
    }
  }, [listingsError, archivedError]);

  // Categorize listings
  const activeListings = userListings.filter(
    (listing: any) => listing.is_active && listing.approval_status === "approved"
  );
  
  const pendingListings = userListings.filter(
    (listing: any) => listing.approval_status === "pending"
  );

  const rejectedListings = userListings.filter(
    (listing: any) => listing.approval_status === "rejected"
  );

  // Inactive listings are those that are not active, not pending, and not rejected
  // These are typically listings deactivated by the user.
  const userDeactivatedListings = userListings.filter(
    (listing: any) => 
      !listing.is_active &&
      listing.approval_status === "approved" // Was approved before deactivation
  );

  // Combine all non-active, non-archived listings for the "Jo Aktive" tab count
  const allInactiveListings = [
      ...pendingListings,
      ...rejectedListings,
      ...userDeactivatedListings
  ];

  const handleViewListing = (id: number) => {
    navigate(`/product/${id}`);
  };

  const handleEditListing = (id: number) => {
    navigate(`/edit-listing/${id}`);
  };

  const processedArchivedListings = archivedListings.map((listing) => ({
    ...listing,
    is_active: false, // Archived listings are inherently inactive
  }));

  useEffect(() => {
    if (processedArchivedListings.length > 0) {
      // console.log("Processed archived listings:", processedArchivedListings);
    }
  }, [processedArchivedListings]);
  
  const handleTogglePremiumWithId = (id: number, isPremium: boolean) => {
    setSelectedListingId(id);
    handleTogglePremium(id, isPremium);
  };

  const handleReactivateListingWrapper = (id: number, isFromArchive: boolean = false, originalId?: number) => {
    handleReactivateListing(id, isFromArchive, originalId);
  };


  const renderListingGrid = (listings: any[], type: 'active' | 'pending' | 'rejected' | 'userDeactivated' | 'archived') => {
    if (listings.length === 0) {
      let emptyTitle = "";
      let emptyDescription = "";
      let action;

      switch(type) {
        case 'active':
          emptyTitle = isOwner ? "Ju nuk keni shpallje aktive" : "Përdoruesi nuk ka shpallje aktive";
          emptyDescription = isOwner ? "Krijoni një shpallje të re për të filluar shitjen." : "Ky përdorues nuk ka shpallje aktive në këtë moment.";
          if (isOwner) action = { label: "Krijo Shpallje", href: "/create-listing" };
          break;
        case 'pending':
           emptyTitle = isOwner ? "Nuk keni shpallje në pritje" : "Përdoruesi nuk ka shpallje në pritje";
           emptyDescription = isOwner ? "Shpalljet që presin miratim do të shfaqen këtu." : "Ky përdorues nuk ka shpallje në pritje.";
           break;
        case 'rejected':
          emptyTitle = isOwner ? "Nuk keni shpallje të refuzuara" : "Përdoruesi nuk ka shpallje të refuzuara";
          emptyDescription = isOwner ? "Shpalljet e refuzuara do të shfaqen këtu." : "Ky përdorues nuk ka shpallje të refuzuara.";
          break;
        case 'userDeactivated':
           emptyTitle = isOwner ? "Nuk keni shpallje të çaktivizuara" : "Përdoruesi nuk ka shpallje të çaktivizuara";
           emptyDescription = isOwner ? "Shpalljet që keni çaktivizuar manualisht do të shfaqen këtu." : "Ky përdorues nuk ka shpallje të çaktivizuara.";
           break;
        case 'archived':
          emptyTitle = "Ju nuk keni shpallje të arkivuara";
          emptyDescription = "Këtu do të shfaqen shpalljet tuaja të arkivuara.";
          break;
      }
      return <EmptyState title={emptyTitle} description={emptyDescription} action={action} />;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {listings.map((listing: any) => (
          <ListingItem
            key={listing.id}
            listing={listing}
            approval_status={listing.approval_status}
            onView={handleViewListing}
            onEdit={isOwner && listing.approval_status !== 'rejected' && listing.approval_status !== 'pending' ? handleEditListing : undefined}
            onToggleSold={isOwner && listing.approval_status !== 'rejected' && listing.approval_status !== 'pending' ? handleToggleSold : undefined}
            onTogglePremium={isOwner && listing.approval_status !== 'rejected' && listing.approval_status !== 'pending' ? handleTogglePremiumWithId : undefined}
            onDelete={isOwner ? (id, isArchivedParam) => handleDeleteListing(id, isArchivedParam || type === 'archived') : undefined}
            onDeactivate={isOwner && listing.is_active && listing.approval_status === 'approved' ? handleDeactivateListing : undefined}
            onReactivate={isOwner && !listing.is_active && listing.approval_status !== 'rejected' && listing.approval_status !== 'pending' ? handleReactivateListingWrapper : undefined}
            isViewOnly={isViewOnly}
            isArchived={type === 'archived'}
          />
        ))}
      </div>
    );
  };


  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">
          {isOwner ? "Shpalljet e Mia" : "Shpalljet e Përdoruesit"}
        </h2>
        {isOwner && (
          <Button
            onClick={() => navigate("/create-listing")}
            className="bg-brand-500 hover:bg-brand-600 flex items-center gap-2"
          >
            <Plus size={16} />
            Krijo Shpallje
          </Button>
        )}
      </div>

      <Tabs
        defaultValue="active"
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList className="mb-4 grid w-full grid-cols-2 sm:grid-cols-3">
          <TabsTrigger value="active">
            <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
            Aktive ({activeListings.length})
          </TabsTrigger>
          <TabsTrigger value="inactive">
             <AlertTriangle className="h-4 w-4 mr-1 text-yellow-500" />
            Jo Aktive ({allInactiveListings.length})
          </TabsTrigger>
          {isOwner && (
            <TabsTrigger value="archived">
              <Archive className="h-4 w-4 mr-1" />
              Arkivuar ({processedArchivedListings.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="active">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-64 bg-gray-200 dark:bg-gray-800 rounded-md animate-pulse"
                ></div>
              ))}
            </div>
          ) : renderListingGrid(activeListings, 'active')}
        </TabsContent>

        <TabsContent value="inactive">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(2)].map((_, i) => (
                <div
                  key={i}
                  className="h-64 bg-gray-200 dark:bg-gray-800 rounded-md animate-pulse"
                ></div>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-yellow-500" />
                  Në Pritje ({pendingListings.length})
                </h3>
                {renderListingGrid(pendingListings, 'pending')}
              </div>
              <Separator />
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <XCircle className="h-5 w-5 mr-2 text-red-500" />
                  Të Refuzuara ({rejectedListings.length})
                </h3>
                {renderListingGrid(rejectedListings, 'rejected')}
              </div>
              <Separator />
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <Archive className="h-5 w-5 mr-2 text-gray-500" />
                  Të Çaktivizuara ({userDeactivatedListings.length})
                </h3>
                {renderListingGrid(userDeactivatedListings, 'userDeactivated')}
              </div>
            </div>
          )}
        </TabsContent>

        {isOwner && (
          <TabsContent value="archived">
            {isLoadingArchived ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="h-64 bg-gray-200 dark:bg-gray-800 rounded-md animate-pulse"
                  ></div>
                ))}
              </div>
            ) : renderListingGrid(processedArchivedListings, 'archived')}
          </TabsContent>
        )}
      </Tabs>

      <ConfirmationDialog
        isOpen={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={
          confirmDialogAction === "delete"
            ? confirmDelete
            : confirmDialogAction === "sold"
            ? confirmToggleSold
            : confirmDeactivate
        }
        title={
          confirmDialogAction === "delete"
            ? "Konfirmo fshirjen"
            : confirmDialogAction === "sold"
            ? "Konfirmo shitjen"
            : "Konfirmo çaktivizimin"
        }
        description={
          confirmDialogAction === "delete"
            ? "A jeni i sigurt që dëshironi të fshini këtë shpallje? Ky veprim nuk mund të kthehet."
            : confirmDialogAction === "sold"
            ? "A jeni i sigurt që dëshironi të shënoni këtë produkt si të shitur?"
            : "A jeni i sigurt që dëshironi të çaktivizoni këtë shpallje?"
        }
        confirmLabel={
          confirmDialogAction === "delete"
            ? "Fshije"
            : confirmDialogAction === "sold"
            ? "Konfirmo"
            : "Çaktivizo"
        }
        cancelLabel="Anulo"
        isDangerous={confirmDialogAction === "delete"}
      />

      <ArchivedListingRestoreDialog
        isOpen={archiveRestoreDialogOpen}
        onClose={() => setArchiveRestoreDialogOpen(false)}
        onConfirm={confirmRestoreArchived}
        hasCredits={hasAvailableCredits()}
      />

      <Sheet open={premiumDialogOpen} onOpenChange={setPremiumDialogOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>
              {premiumVouchersCount > 0 ? "Opsionet Premium" : "Pagesa Premium"}
            </SheetTitle>
          </SheetHeader>
          <Separator className="my-4" />
          {selectedListingId && (
            <>
              {premiumVouchersCount > 0 ? (
                <PremiumOptions
                  onSelect={handlePremiumSuccess}
                  onClose={() => setPremiumDialogOpen(false)}
                  listingId={selectedListingId}
                />
              ) : (
                <PremiumPayment
                  onSuccess={handlePremiumSuccess}
                  onCancel={() => setPremiumDialogOpen(false)}
                  onClose={() => setPremiumDialogOpen(false)}
                  listingId={selectedListingId}
                />
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      <PremiumConfirmationDialog
        isOpen={premiumConfirmDialogOpen}
        onClose={() => setPremiumConfirmDialogOpen(false)}
        onConfirm={confirmPremiumUpgrade}
        hasVouchers={premiumVouchersCount > 0}
      />
    </div>
  );
};

export default UserListings;
